import React from 'react';
import { calculateSPRSScore, getSPRSStatus, getSPRSColor } from './data/controlWeights';

// Position on the -203 to +110 scale as a percentage (0–100)
function scorePct(score) {
  return Math.max(0, Math.min(100, ((score + 203) / 313) * 100));
}

const DOD_MIN_PCT = ((88 + 203) / 313) * 100;   // ≈ 93.3%

export default function SPRSScore({ answers, compact = false }) {
  const score  = calculateSPRSScore(answers);
  const status = getSPRSStatus(score);
  const color  = getSPRSColor(score);
  const pct    = scorePct(score);

  if (compact) {
    return (
      <div style={{
        background: 'var(--bg2)', border: `1px solid ${color}40`,
        borderRadius: 10, padding: '14px 18px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div>
          <div style={{
            fontSize: 10, color: 'var(--text3)', marginBottom: 2,
            textTransform: 'uppercase', letterSpacing: '0.07em',
          }}>
            SPRS Score
          </div>
          <div style={{ fontSize: 28, fontWeight: 700, color, lineHeight: 1 }}>
            {score > 0 ? `+${score}` : score}
          </div>
          <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 3 }}>
            range −203 to +110
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <span style={{
            fontSize: 11, fontWeight: 600, padding: '4px 10px',
            borderRadius: 20, background: status.bg, color: status.color,
            border: `1px solid ${status.color}`,
          }}>
            {status.label}
          </span>
          <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 6 }}>
            DoD min: 88
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      background: 'var(--bg2)', border: '1px solid var(--border)',
      borderRadius: 10, padding: 20,
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', justifyContent: 'space-between',
        alignItems: 'flex-start', marginBottom: 20,
      }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>
            SPRS Score
          </div>
          <div style={{ fontSize: 11, color: 'var(--text3)' }}>
            Supplier Performance Risk System — DoD required score
          </div>
        </div>
        <span style={{
          fontSize: 11, fontWeight: 600, padding: '4px 10px',
          borderRadius: 20, background: status.bg, color: status.color,
          border: `1px solid ${status.color}`,
        }}>
          {status.label}
        </span>
      </div>

      {/* Big Score Display */}
      <div style={{ textAlign: 'center', marginBottom: 24 }}>
        <div style={{
          fontSize: 72, fontWeight: 800, color, lineHeight: 1, marginBottom: 4,
          letterSpacing: '-2px',
        }}>
          {score > 0 ? `+${score}` : score}
        </div>
        <div style={{ fontSize: 12, color: 'var(--text3)' }}>
          SPRS range: −203 (min) to +110 (max)
        </div>
        {score >= 88 && (
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 8,
            background: 'rgba(34,197,94,0.12)', border: '1px solid #22c55e',
            borderRadius: 20, padding: '4px 14px',
            fontSize: 11, fontWeight: 700, color: '#22c55e',
          }}>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
            Audit Ready — Meets DoD 88-Point Threshold
          </div>
        )}
        {score < 88 && (
          <div style={{
            marginTop: 8, fontSize: 12,
            color: score < 0 ? '#ef4444' : '#f59e0b',
            fontWeight: 600,
          }}>
            {score < 0
              ? `${88 - score} points needed to reach DoD minimum (currently ${score})`
              : `${88 - score} more point${88 - score !== 1 ? 's' : ''} needed to reach DoD minimum`
            }
          </div>
        )}
      </div>

      {/* Score Bar — marker style */}
      <div style={{ marginBottom: 20 }}>
        {/* Track */}
        <div style={{
          position: 'relative', height: 14,
          background: 'var(--surface2)', borderRadius: 7,
          overflow: 'visible',
        }}>
          {/* Filled region from -203 to score */}
          <div style={{
            position: 'absolute', left: 0, top: 0, bottom: 0,
            width: `${pct}%`,
            background: color, opacity: 0.25,
            borderRadius: '7px 0 0 7px',
            transition: 'width 0.6s',
          }} />

          {/* Score position marker */}
          <div style={{
            position: 'absolute',
            left: `${pct}%`,
            top: -3, bottom: -3,
            width: 4,
            background: color,
            borderRadius: 2,
            transform: 'translateX(-50%)',
            boxShadow: `0 0 6px ${color}80`,
            transition: 'left 0.6s',
            zIndex: 2,
          }} />

          {/* DoD minimum line at 88 */}
          <div style={{
            position: 'absolute',
            left: `${DOD_MIN_PCT}%`,
            top: -6, bottom: -6,
            width: 2,
            background: '#22c55e',
            borderRadius: 1,
            zIndex: 3,
          }} />
        </div>

        {/* Labels row */}
        <div style={{ position: 'relative', height: 24, marginTop: 4 }}>
          <span style={{ position: 'absolute', left: 0, fontSize: 10, color: 'var(--text3)' }}>
            −203
          </span>
          {/* DoD Min label pinned to the 88-point line */}
          <span style={{
            position: 'absolute',
            left: `${DOD_MIN_PCT}%`,
            transform: 'translateX(-50%)',
            fontSize: 10, fontWeight: 700,
            color: '#22c55e',
            whiteSpace: 'nowrap',
          }}>
            DoD Minimum (88)
          </span>
          <span style={{ position: 'absolute', right: 0, fontSize: 10, color: 'var(--text3)' }}>
            +110
          </span>
        </div>
      </div>

      {/* Score Breakdown */}
      <div style={{
        display: 'grid', gridTemplateColumns: '1fr 1fr 1fr',
        gap: 8, marginBottom: 16,
      }}>
        {[
          {
            label: 'Current Score',
            value: score > 0 ? `+${score}` : String(score),
            color: color,
            isRaw: true,
          },
          {
            label: 'Points to DoD Min (88)',
            value: Math.max(0, 88 - score),
            color: '#f59e0b',
            isRaw: false,
          },
          {
            label: 'Points to Perfect (110)',
            value: Math.max(0, 110 - score),
            color: '#3b82f6',
            isRaw: false,
          },
        ].map(item => (
          <div key={item.label} style={{
            background: 'var(--surface)', borderRadius: 8,
            padding: '10px 12px', textAlign: 'center',
          }}>
            <div style={{
              fontSize: 20, fontWeight: 700,
              color: item.isRaw ? item.color : (item.value === 0 ? '#22c55e' : item.color),
              marginBottom: 4,
            }}>
              {item.isRaw ? item.value : (item.value === 0 ? '✓' : item.value)}
            </div>
            <div style={{ fontSize: 10, color: 'var(--text3)' }}>{item.label}</div>
          </div>
        ))}
      </div>

      {/* Status Message */}
      <div style={{
        background: status.bg, border: `1px solid ${status.color}`,
        borderRadius: 8, padding: '12px 14px', fontSize: 12,
        color: status.color, lineHeight: 1.5,
      }}>
        {score >= 110 && (
          <span>Perfect SPRS score — all 110 controls are fully implemented. Your organization is fully prepared for C3PAO assessment.</span>
        )}
        {score >= 88 && score < 110 && (
          <span>Your SPRS score meets the DoD 88-point threshold for C3PAO assessment eligibility. Keep your score current through annual self-assessment affirmations and CMMC Level 2 certification.</span>
        )}
        {score >= 0 && score < 88 && (
          <span>You need {88 - score} more point{88 - score !== 1 ? 's' : ''} to reach the DoD minimum of 88. Focus on high-weight controls (−5 and −3 point controls) first — they recover the most points per fix.</span>
        )}
        {score < 0 && (
          <span>Your score is below zero, indicating critical cybersecurity gaps. You need {88 - score} points to reach DoD minimum. Prioritize −5 weight controls (MFA, encryption) immediately.</span>
        )}
      </div>

      {/* DFARS 7019 Regulatory Note */}
      <div style={{
        marginTop: 12, padding: '10px 14px', borderRadius: 8,
        background: 'var(--blue-bg)', border: '1px solid rgba(45,125,210,0.2)',
        fontSize: 11, color: 'var(--accent)', lineHeight: 1.6,
      }}>
        <strong>Regulatory Update:</strong> The DFARS 7019 Basic Assessment upload requirement was removed as of February 1, 2026.
        The current requirement is CMMC Level 2 Self-Assessment or Third-Party Assessment (C3PAO).
        Keep your SPRS record current through annual affirmations.
      </div>
    </div>
  );
}
